import { TagIcon } from "assets/icons";
import Button from "components/foundation/Button";
import Label from "components/foundation/Label/Label";
import Modal from "components/foundation/Modal/Modal";
import { useAddOrGetFreeTagMutation } from "features/document/hooks/mutations/useAddOrGetFreeTagMutation";
import React, { useRef, useState } from "react";
import { useRecoilState } from "recoil";
import { documentFreeTagsState } from "store/document/documentCommon";
import styled from "styled-components";

const MAX_LENGTH = 50;

interface FreeTagEditorProps extends React.HTMLAttributes<HTMLDivElement> {
  isTitle?: boolean;
}

const FreeTagEditor = ({ isTitle = true, ...rest }: FreeTagEditorProps) => {
  const [freeTags, setFreeTags] = useRecoilState(documentFreeTagsState);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Free 태그 추가
  const addOrGetFreeTagMutation = useAddOrGetFreeTagMutation();
  const addOrGetFreeTag = (name: string) => {
    addOrGetFreeTagMutation.mutate(
      {
        name,
      },
      {
        onSuccess: (res) => {
          const uuid = res.data.result;
          setFreeTags((prev) => [
            ...prev,
            {
              uuid,
              name,
            },
          ]);

          if (inputRef.current) inputRef.current.value = "";
          setIsEmpty(true);
        },
      },
    );
  };

  const [openTagMaxLengthModal, setOpenTagMaxLengthModal] = useState(false);
  const onKeyUp: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    const { key } = event;
    const { value } = event.currentTarget;

    if (key === "Enter") {
      if (freeTags.length >= MAX_LENGTH) {
        setOpenTagMaxLengthModal(true);
        return;
      }

      if (value) addOrGetFreeTag(value);
    }
  };

  /**
   * @description 추가 버튼 클릭 이벤트 핸들러
   */
  const onClickAdd = () => {
    if (!inputRef.current) return;

    const { value } = inputRef.current;
    if (value) addOrGetFreeTag(value);
    setIsEmpty(true);
  };

  const [isEmpty, setIsEmpty] = useState(true);
  const isInputEmpty = () => {
    !inputRef.current || inputRef.current.value.trim() === ""
      ? setIsEmpty(true)
      : setIsEmpty(false);
  };

  /**
   * @description 제거 버튼 클릭 이벤트 핸들러
   */
  const onClickRemove = (tagUuid: string) => {
    setFreeTags((prev) => prev.filter((tag) => tag.uuid !== tagUuid));
  };

  return (
    <>
      <AddTagSectionStyle {...rest}>
        {isTitle && <AddTagSectionTitleStyle>자유태그</AddTagSectionTitleStyle>}
        <FreeTagBoxStyle>
          <TagIconStyle />
          <TagListStyle>
            {freeTags.map((tag) => (
              <Label
                key={tag.uuid}
                variant="grey"
                onClickRemove={() => onClickRemove(tag.uuid)}
              >
                {tag.name}
              </Label>
            ))}
            <TagInputStyle
              ref={inputRef}
              placeholder="태그를 추가하세요."
              onKeyUp={onKeyUp}
              onChange={() => isInputEmpty()}
            />
          </TagListStyle>

          <Button
            variant="tertiary"
            disabled={isEmpty}
            onClick={() => onClickAdd()}
          >
            추가
          </Button>
        </FreeTagBoxStyle>
      </AddTagSectionStyle>

      <Modal.Root
        open={openTagMaxLengthModal}
        onOpenChange={setOpenTagMaxLengthModal}
      >
        <Modal.Body
          title="자유태그 제한 개수 초과"
          onCancel={() => setOpenTagMaxLengthModal(false)}
        >
          자유태그는 최대 {MAX_LENGTH || 0}개까지 추가 가능합니다.
        </Modal.Body>
      </Modal.Root>
    </>
  );
};

export default FreeTagEditor;

/* 스타일 */
const AddTagSectionStyle = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const AddTagSectionTitleStyle = styled.div`
  width: 19.7rem;
  ${({ theme }) => theme.typography.body1.regular}
  letter-spacing: -0.048rem;
`;

const FreeTagBoxStyle = styled.div`
  background: ${({ theme }) => theme.color.background.dark};
  width: 100%;
  display: flex;
  padding: 1.6rem;
  gap: 1rem;
  flex: 1;
  border-radius: 1rem;
`;

const TagIconStyle = styled(TagIcon)`
  margin-top: 0.8rem;

  color: ${({ theme }) => theme.color.text.tertiery};
`;

const TagListStyle = styled.div`
  margin-top: 0.4rem;

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.8rem;
  flex: 1;
`;

const TagInputStyle = styled.input`
  background: none;
  border: none;
  outline: none;
  height: 3.2rem;

  ${({ theme }) => theme.typography.body1.regular}

  &::placeholder {
    color: ${({ theme }) => theme.color.text.placeholder};
  }
`;
